import React from 'react'
import Accordion from '@mui/material/Accordion'
import AccordionDetails from '@mui/material/AccordionDetails'
import AccordionSummary from '@mui/material/AccordionSummary'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import Stack from '@mui/material/Stack'
import { useTheme } from '@mui/material/styles'

import { H3, P, Text } from '@mxenabled/mxui'
import { ExpandMore, ChevronRight } from '@mxenabled/mx-icons'

import { observer } from 'mobx-react-lite'

import { Loader, useFinstrongStore, useGlobalCopyStore, useGlobalUiStore } from '../common'
import { useDimensions } from '../common/hooks/useDimensions'

import { ConnectMoreAccountsCard } from './components/ConnectMoreAccountsCard'
import { FinancialStrengthGraph } from './components/FinancialStrengthGraph'
import { calculateFinstrongScoreStatus } from './util/finstrongUtils'
import ScoreRubricDrawer from './components/ScoreRubricDrawer'

export const FinancialStrengthCard = observer(() => {
  const [isScoreRubricDrawerOpen, setIsScoreRubricDrawerOpen] = React.useState(false)
  const theme = useTheme()
  const [containerRef, { width }] = useDimensions()
  const { finstrong: copy } = useGlobalCopyStore()
  const { isCopyLoaded, isInitialized } = useGlobalUiStore()

  const { healthScore, healthScoreChange } = useFinstrongStore()
  const isDarkMode = theme.palette.mode === 'dark'

  const healthScoreStatus = calculateFinstrongScoreStatus(healthScore?.health_score, copy)
  if (!isCopyLoaded || !isInitialized) return <Loader />
  return (
    <Card ref={containerRef} sx={{ p: 16, display: 'flex', flexDirection: 'column', gap: 8 }}>
      <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
        <H3>{copy.financial_strength_header}</H3>

        <Button
          endIcon={<ChevronRight />}
          onClick={() => setIsScoreRubricDrawerOpen(true)}
          size="small"
          sx={{
            '&.MuiButtonBase-root': {
              px: 8,
              py: 4,
            },
          }}
          variant="text"
        >
          <Text
            bold={true}
            sx={{
              overflow: 'visible',
            }}
            variant="body2"
          >
            {copy.financial_strength_header_action}
          </Text>
        </Button>
      </Stack>
      <Stack sx={{ justifyContent: 'center', alignItems: 'center', pb: 4, pt: 20, px: 16 }}>
        <FinancialStrengthGraph
          arcRadius={width <= 323 ? 72.5 : 154}
          footerCenterContent={healthScoreChange}
          healthScore={healthScore?.health_score || 0}
          size={width <= 323 ? 'M' : 'L'}
        />
      </Stack>
      {!healthScore?.health_score ? (
        <ConnectMoreAccountsCard
          buttonText={copy.connect_more_accounts_title}
          description={copy.health_score_zero_description}
          drawerType="connectDrawer"
          hasIcon={false}
          isDashboard={true}
          sx={{
            backgroundColor: isDarkMode ? 'grey.800' : 'grey.50', //TODO: Need to change the background color to palette (supporting both darkmode and lightmode)
            '.connect-more-accounts-title': {
              fontWeight: 600,
              lineHeight: '20px',
              color: 'text.primary',
            },
            '.connect-more-accounts-description': { color: 'text.primary' },
          }}
          title={copy.health_score_zero_title}
        />
      ) : (
        <Accordion
          sx={{
            px: 16,
            backgroundColor: isDarkMode ? 'grey.800' : 'grey.50', //TODO: Need to change the background color to palette (supporting both darkmode and lightmode)
            '&.Mui-expanded': {
              m: 0,
            },
            '&::before': {
              display: 'none',
            },
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMore size={20} />}
            sx={{
              '&.MuiAccordionSummary-root': {
                minHeight: 0,
              },
              height: 44,
              p: 0,
              '&.Mui-expanded': {
                minHeight: 0,
              },
            }}
          >
            <P sx={{ fontWeight: 600 }} variant="body1">
              {copy.accordion_title_text}
            </P>
          </AccordionSummary>
          <AccordionDetails sx={{ '&.MuiAccordionDetails-root': { padding: 0, pb: 16 } }}>
            <P variant="subtitle1">{healthScoreStatus.scoreContent}</P>
          </AccordionDetails>
        </Accordion>
      )}
      <ScoreRubricDrawer
        isOpen={isScoreRubricDrawerOpen}
        onClose={() => setIsScoreRubricDrawerOpen(false)}
      />
    </Card>
  )
})
